import invariant from "tiny-invariant";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import CombineOperator from "@mapmycustomers/shared/types/viewModel/internalModel/CombineOperator";
import FilterModel, {
  CombinedCondition,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import AgGridFilterOperator from "@app/enum/AgGridFilterOperator";
import AgGridCombineOperator from "@app/types/viewModel/agGridModel/AgGridCombineOperator";
import AgGridFilterModel, {
  AgGridCondition,
} from "@app/types/viewModel/agGridModel/AgGridFilterModel";

import UserField from "../fieldModel/impl/UserField";

import { isCombinedCondition } from "./assert";
import { SET_FILTER_OPERATOR } from "./convertFromAgGridFilterModel";

export const filterOperatorToAgGridFilterOperatorMap: Partial<{
  [key in FilterOperator]: AgGridFilterOperator;
}> = {
  [FilterOperator.CONTAINS]: AgGridFilterOperator.CONTAINS,
  [FilterOperator.DOES_NOT_END_WITH]: AgGridFilterOperator.ENDS_WITH,
  [FilterOperator.DOES_NOT_START_WITH]: AgGridFilterOperator.STARTS_WITH,
  [FilterOperator.ENDS_WITH]: AgGridFilterOperator.ENDS_WITH,
  [FilterOperator.EQUALS]: AgGridFilterOperator.EQUALS,
  [FilterOperator.GREATER_THAN]: AgGridFilterOperator.GREATER_THAN,
  [FilterOperator.GREATER_THAN_OR_EQUAL]: AgGridFilterOperator.GREATER_THAN_OR_EQUAL,
  [FilterOperator.IN_RANGE]: AgGridFilterOperator.IN_RANGE,
  [FilterOperator.LESS_THAN]: AgGridFilterOperator.LESS_THAN,
  [FilterOperator.LESS_THAN_OR_EQUAL]: AgGridFilterOperator.LESS_THAN_OR_EQUAL,
  [FilterOperator.NOT_CONTAINS]: AgGridFilterOperator.NOT_CONTAINS,
  [FilterOperator.NOT_EQUAL]: AgGridFilterOperator.NOT_EQUAL,
  [FilterOperator.STARTS_WITH]: AgGridFilterOperator.STARTS_WITH,
} as const;

export const combineOperatorToAgGridCombineOperatorMap: {
  [key in CombineOperator]: AgGridCombineOperator;
} = {
  AND: "AND",
  OR: "OR",
} as const;

const getFilterType = (field: IField) => {
  if (field.type === FieldType.NUMBER) {
    return "number";
  } else if (field.type === FieldType.DATE || field.type === FieldType.DATE_TIME) {
    return "date";
  } else if (field.type === FieldType.STRING) {
    return "text";
  } else {
    invariant(false, `Unknown filter type for field "${field.name}", type: ${field.type}`);
  }
};

const convertToAgGridCondition = (condition: SimpleCondition, field: IField): AgGridCondition => {
  if (field.hasFeature(FieldFeature.GROUP_FIELD)) {
    // ignoring because of narrower operators field in GroupsFilterCondition
    // @ts-ignore
    return {
      filterType: "groupsFilter",
      ...condition,
    };
  }

  if (condition.operator === FilterOperator.EMPTY) {
    return { filterType: "emptiness", type: AgGridFilterOperator.EQUALS };
  }

  if (condition.operator === FilterOperator.NOT_EMPTY) {
    return { filterType: "emptiness", type: AgGridFilterOperator.NOT_EQUAL };
  }

  if (field.hasFeature(FieldFeature.GEO_POINT_FIELD)) {
    // ignoring because of narrower operators field in AreaFilterCondition
    // @ts-ignore
    return {
      filterType: "areaFilter",
      ...condition,
    };
  }

  if (
    field.hasFeature(FieldFeature.CUSTOM_FIELD) &&
    [FilterOperator.IN_ALL, FilterOperator.IN_ANY].includes(condition.operator)
  ) {
    // ignoring because of narrower operators field in OptionsCondition
    // @ts-ignore
    return {
      filterType: "optionsFilter",
      ...condition,
    };
  }

  // TODO: move some of this logic inside fields themselves. Make it possible to override it
  if (
    condition.operator === SET_FILTER_OPERATOR ||
    (condition.operator === FilterOperator.CONTAINS && field.hasFeature(FieldFeature.USES_COLORS))
  ) {
    return {
      filterType: "set",
      values: condition.value,
    };
  }

  const type = filterOperatorToAgGridFilterOperatorMap[condition.operator];
  invariant(
    type,
    `Failed to convert operator condition, field: "${field.name}", operator: "${condition.operator}"`
  );

  if (
    [FilterOperator.DOES_NOT_END_WITH, FilterOperator.DOES_NOT_START_WITH].includes(
      condition.operator
    )
  ) {
    return {
      filter: condition.value,
      filterType: "negativeTextFilter",
      type,
    };
  }

  if (field.type === FieldType.NUMBER || field instanceof UserField) {
    return {
      filter: condition.operator === FilterOperator.IN_RANGE ? condition.value[0] : condition.value,
      filterTo: condition.operator === FilterOperator.IN_RANGE ? condition.value[1] : undefined,
      filterType: "number",
      type,
    };
  } else if (field.type === FieldType.STRING) {
    return {
      filter: condition.value,
      filterType: "text",
      type,
    };
  } else if (field.type === FieldType.DATE || field.type === FieldType.DATE_TIME) {
    return {
      dateFrom:
        condition.operator === FilterOperator.IN_RANGE ? condition.value[0] : condition.value,
      dateTo: condition.operator === FilterOperator.IN_RANGE ? condition.value[1] : undefined,
      filterType: "date",
      type,
    };
  } else {
    invariant(
      false,
      `Tried to convert filter for field unsupported by AgGrid: "${field.name}", type: ${field.type}`
    );
  }
};

const convertSimpleConditionToAgGridCondition = (
  field: IField,
  condition: SimpleCondition,
  gridFilterModel: AgGridFilterModel
) => {
  invariant(
    !(field.filterName in gridFilterModel),
    `Field ${field.filterName} is present twice in aggrid field model`
  );
  gridFilterModel[field.filterName] = convertToAgGridCondition(condition, field);
};

const convertCombinedConditionToAgGridCondition = (
  field: IField,
  condition: CombinedCondition,
  gridFilterModel: AgGridFilterModel
) => {
  invariant(
    !(field.filterName in gridFilterModel),
    `Field ${field.filterName} is present twice in aggrid field model`
  );
  gridFilterModel[field.filterName] = {
    // @ts-ignore
    condition1: convertToAgGridCondition(condition.conditions[0], field),
    // @ts-ignore
    condition2: convertToAgGridCondition(condition.conditions[1], field),
    filterType: getFilterType(field),
    operator: combineOperatorToAgGridCombineOperatorMap[condition.operator],
  };
};

const convertToAgGridFilterModel = (
  filterModel: Partial<FilterModel>,
  fieldModel: IFieldModel
): Partial<AgGridFilterModel> => {
  const gridFilterModel: Partial<AgGridFilterModel> = {};
  Object.keys(filterModel).forEach((fieldFilterName) => {
    const field = fieldModel.getByFilterName(fieldFilterName);
    invariant(field, `Unknown field name: ${fieldFilterName}`);
    const condition = filterModel[fieldFilterName];
    if (condition === undefined) {
      gridFilterModel[field.name] = undefined;
      return;
    }

    if (isCombinedCondition(condition)) {
      // @ts-ignore
      convertCombinedConditionToAgGridCondition(field, condition, gridFilterModel);
    } else {
      // @ts-ignore
      convertSimpleConditionToAgGridCondition(field, condition, gridFilterModel);
    }
  });

  return gridFilterModel;
};

export default convertToAgGridFilterModel;
